import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
	AttachmentRef,
	InputMaybe,
	PogisDate,
	PogisDocument,
	Scalars,
} from 'types';
import {useSearchParams} from 'react-router-dom';
import {createSearchParams, useNavigate} from 'react-router-dom';
import {
	InfinityList,
	InfinityListView,
} from 'components/InfinityList/InfinityList';
import {DEFAULT_DATE} from 'components/EntityList/EntityUtils';
import {
	useGetPogisDocumentsNextLazyQuery,
	useGetPogisDocumentsQueryLazyQuery,
	useGetPogisSortOrdersLazyQuery,
	useGetPogisSortOrdersQuery,
} from './hooks/PogisDocuments.generated';
import {SelectionMode} from 'components/FluentUICompat';
import {useInfinityListStandardHooks} from 'components/InfinityList/InfinitListStandardHooks';
import {
	usePogisColumnMetaData,
	usePogisPageColumn,
	usePogisPageFilterOptions,
} from './PogisPageColumns';
import {handleClickPOGISPage} from 'components/InfinityList/Excel/XLPOGISArchivPage';

export type PogisDocumentModified = {
	id: Scalars['String'];
	pogisId: Scalars['String'];
	keywords: Array<Scalars['String']>;
	markets: Array<Scalars['String']>;
	regulationStatus: Scalars['String'];
	shortInfo: Scalars['String'];
	effectiveDate?: InputMaybe<Scalars['DateTime']>;
	ntDate?: InputMaybe<Scalars['DateTime']>;
	nfDate?: InputMaybe<Scalars['DateTime']>;
	modelYear?: InputMaybe<Scalars['DateTime']>;
	otherDates?: Array<PogisDate>;
	standards?: InputMaybe<Array<Scalars['String']>>;
	associations?: InputMaybe<Array<Scalars['String']>>;
	relatedDocuments?: InputMaybe<Array<Scalars['String']>>;
	editor: Scalars['String'];
	docStatus: Scalars['String'];
	pogisAttachments?: InputMaybe<AttachmentRef[]>;
};

const initialSortField = 'pogisId';
const initialSortOrder = 'DESC';
const intBatchSize = 50;

// *page object ----------------------------------------------------------------------------------------------------------------------
const PogisDocumentPage: React.FC = () => {
	// *hooks for states --------------------------------------------------------------------------------------------------------------
	const {t} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'PogisDocumentsList',
	});
	const {t: tViews} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'Views',
	});

	const {t: tExcel} = useTranslation();

	const [getSortOrder] = useGetPogisSortOrdersLazyQuery();

	const [gePogisDocumentQuery, {loading: loadingQuery, data: dataQuery}] =
		useGetPogisDocumentsQueryLazyQuery();

	const [getPogisDocumentsNext, {loading: loadingDocuments}] =
		useGetPogisDocumentsNextLazyQuery();

	const [searchParams] = useSearchParams();

	const navigate = useNavigate();

	const views: InfinityListView[] = useMemo(
		() => [
			{headerText: tViews('STD'), accessKey: 'STD', query: ''},
			{
				headerText: tViews('ONLYPOGIS'),
				accessKey: 'ONLYPOGIS',
				query: `{regulationStatus : { $ne: 'GSO-Eintrag'}}`,
			},
			{
				headerText: tViews('ONLYGSO'),
				accessKey: 'ONLYGSO',
				query: `{regulationStatus : { $eq: 'GSO-Eintrag'}}`,
			},
		],
		[],
	);

	const initialView =
		searchParams.getAll('view').length > 0
			? searchParams.getAll('view').at(0) ?? ''
			: 'STD';

	const arrIds = useMemo(
		() => dataQuery?.pogisDocumentsQuery.pogisIds ?? [],
		[dataQuery],
	);

	const columns = usePogisPageColumn();
	const columnMetaData = usePogisColumnMetaData();
	const columnFilterOptions = usePogisPageFilterOptions(dataQuery);

	const queryDocumentsDB = async (
		query: string,
		sortField: string,
		sortOrder: string,
	) => {
		const performDBQuery = initialView !== 'STD' || query !== '';
		const {data: dataQuery} = await gePogisDocumentQuery({
			variables: {
				queryArgs: {query, sortField, sortOrder},
			},
		});

		const arrDocumentNext = (
			dataQuery?.pogisDocumentsQuery.pogisDocumentsBatch ?? []
		).map(x => funcMapPogisDocument(x as PogisDocument));
		const arrIDsNew = dataQuery?.pogisDocumentsQuery.pogisIds ?? [];
		return {arrDocumentNext, arrIDsNew};
	};

	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const {data: dataDocumentsNext} = await getPogisDocumentsNext({
			variables: {nextArgs: {preFilteredIds}},
		});

		return (dataDocumentsNext?.pogisDocumentsNext ?? []).map(x =>
			funcMapPogisDocument(x as PogisDocument),
		);
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const {data: dataSortOrder} = await getSortOrder({
			variables: {
				sortArgs: {sortField, preFilteredIds},
			},
		});

		return dataSortOrder?.pogisSortOrders ?? preFilteredIds;
	};

	const handleExcelExport = async (arrDocuments: PogisDocumentModified[]) => {
		await handleClickPOGISPage(
			arrDocuments,
			columns.filter(x => x.hiddenAlways === undefined).map(x => x.name),
			tExcel,
		);
	};

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		setArrDocument,
		setlastFetchedId,
		setCursor,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
		excelExportSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds,
		columns,
		columnMetaData,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
		handleExcelExport,
	});

	const handleViewingItem = async (pogis: PogisDocumentModified) => {
		navigate({
			pathname: '',
			search: createSearchParams({
				id: pogis.pogisId,
			}).toString(),
		});
		setArrDocument([pogis]);
		setlastFetchedId(pogis.id);
		setCursor(0);
	};

	const createHref = (item: unknown) =>
		`?id=${(item as PogisDocumentModified).pogisId}`;

	const numberOfElements = arrIds.length ?? 1;

	// *react object ------------------------------------------------------------------------------------------------------------------
	return (
		<InfinityList
			pageName={t('Archive')}
			items={arrDocument}
			sticky={true}
			columns={columns}
			columnMetaData={columnMetaData}
			columnFilterOptions={columnFilterOptions}
			selectionMode={SelectionMode.none}
			stillLoading={loadingQuery || loadingDocuments}
			initalFilters={linksFilters}
			initialSortField={initialSortField}
			initialSortOrder={initialSortOrder}
			initialView={initialView}
			keyPrefix={keyPrefix}
			firstRender={firstRenderSTD}
			loadItemsNext={loadItemsNextSTD}
			applyFilter={applyFilterSTD}
			applySort={applySortSTD}
			excelExport={excelExportSTD}
			handleInvokeItem={handleViewingItem}
			createHref={createHref}
			numberOfElements={numberOfElements}
			views={views}
		/>
	);
};

const fieldsFilterDateMongo = [
	'Effective Date',
	'New Registration',
	'New Types',
	'Model Year',
];

export const funcMapPogisDocument = (
	pogisDocument: PogisDocument,
): PogisDocumentModified => {
	const pogisdates = pogisDocument.dates;
	const effDates = pogisdates.filter(x => x.dateType === 'Effective Date');
	const nfDates = pogisdates.filter(x => x.dateType === 'New Registration');
	const ntDates = pogisdates.filter(x => x.dateType === 'New Types');
	const myDates = pogisdates.filter(x => x.dateType === 'Model Year');
	const restDates = pogisdates.filter(
		x => !fieldsFilterDateMongo.includes(x.dateType),
	);

	const effDate = effDates.length > 0 ? effDates[0].date : DEFAULT_DATE;
	const nfDate = nfDates.length > 0 ? nfDates[0].date : DEFAULT_DATE;
	const ntDate = ntDates.length > 0 ? ntDates[0].date : DEFAULT_DATE;
	const myDate = myDates.length > 0 ? myDates[0].date : DEFAULT_DATE;

	const pogisDocMapped: PogisDocumentModified = {
		...pogisDocument,
		effectiveDate: effDate,
		ntDate,
		nfDate,
		modelYear: myDate,
		otherDates: restDates,
		pogisAttachments: pogisDocument.pogisAttachments ?? [],
	};
	return pogisDocMapped;
};

export default PogisDocumentPage;
